import { Injectable } from '@angular/core';
import { MerchandiseService } from '@epicuro-next/data/merchandise';
import { IntercomService } from '@epicuro-next/platform/intercom';
import { loadAccountDetailsSuccess } from '@epicuro-next/state/accounts-state';
import {
  selectAccountId,
  selectSiteId,
} from '@epicuro-next/state/session-state';
import {
  logout,
  selectIsSalesRep,
  selectUser,
} from '@epicuro-next/state/user-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { filter, map, switchMap, tap } from 'rxjs/operators';

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private intercom: IntercomService,
    private store: Store,
    private merchandiseService: MerchandiseService,
  ) {}

  public loadAccountDetailsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadAccountDetailsSuccess),
        concatLatestFrom(() => [
          this.intercom.isBooted$,
          this.store.pipe(select(selectUser)),
          this.store.pipe(select(selectIsSalesRep)),
          this.store.pipe(select(selectSiteId)),
          this.store.pipe(select(selectAccountId)),
        ]),
        filter(([, booted]) => booted),
        switchMap(([{ account }, , user, isSalesRep, siteId, accountId]) => {
          return this.merchandiseService
            .getIntercom(siteId, accountId ?? '')
            .pipe(
              map((result: string) => {
                const codes = result.toLowerCase().split(',');
                const inCodes = account.shipToAddresses.some(
                  (address) =>
                    address.countryCode &&
                    codes.includes(address.countryCode.toLowerCase()),
                );

                if (!inCodes) {
                  this.intercom.shutdown();

                  return;
                }

                this.intercom.update({
                  email: user?.userId,
                  name: user?.displayName,
                  user_id: user?.userId,
                  accountId: account.externalId,
                  accountName: account.name,
                  domainId: account.domainId,
                  language_override: user?.preferredCultureInfo,
                  salesrep: isSalesRep,
                });
              }),
            );
        }),
      ),
    { dispatch: false },
  );

  public logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logout),
        concatLatestFrom(() => [this.intercom.isBooted$]),
        filter(([, isBooted]) => isBooted),
        tap(() => {
          this.intercom.shutdown();
        }),
      ),
    { dispatch: false },
  );
}
